import { Box, Flex, Text, useBreakpointValue, useToast } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import CollapsibleCard from '../../../../../../components/CollapsibleCard';
import { GetLeadByExternalId200 } from '../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalId';
import MarketplaceCard from '../../MarketplaceCard';
import ProgressTrack from './ProgressTrack';
import SigningSettlementLine from './SigningSettlementLine';
import StageNode from './StageNode';
import {
  AcceptedOffer,
  StageView,
  currentStageKey,
  resolveStages,
  stageTimestampsFromTimeline,
  subtitleFor,
} from './utils';

type SaleProgressTimelineProps = {
  lead: GetLeadByExternalId200;
  requiredRemaining: number;
  acceptedOffer?: AcceptedOffer;
};

const StageLabelBlock = ({
  stage,
  subtitle,
  centered,
}: {
  stage: StageView;
  subtitle: string;
  centered?: boolean;
}) => {
  const isDone = stage.state === 'done';
  const isUpcoming = stage.state === 'upcoming';
  return (
    <>
      <Text
        fontFamily="heading"
        fontSize="14px"
        fontWeight={isDone || isUpcoming ? 600 : 700}
        letterSpacing="-0.01em"
        color={isDone ? 'charcoal' : isUpcoming ? 'grayMedium' : 'leaseEndBlue'}
        textAlign={centered ? 'center' : undefined}
      >
        {stage.label}
      </Text>
      {subtitle && (
        <Text
          mt="2px"
          fontSize="12px"
          lineHeight="1.5"
          fontWeight={isDone ? 600 : 400}
          color={isDone ? 'greenDark' : isUpcoming ? 'grayMedium' : 'charcoal'}
          textAlign={centered ? 'center' : undefined}
        >
          {subtitle}
        </Text>
      )}
    </>
  );
};

const SaleProgressTimeline = ({
  lead,
  requiredRemaining,
  acceptedOffer,
}: SaleProgressTimelineProps) => {
  const [displayStatus, setDisplayStatus] = useState(lead.status);
  const toast = useToast();
  const toastPosition = useBreakpointValue({ base: 'top', md: 'bottom' } as const) ?? 'bottom';

  useEffect(() => {
    if (lead.status === displayStatus) return undefined;
    const advancingToSigning =
      currentStageKey(displayStatus) === 'finish' && currentStageKey(lead.status) === 'signing';
    if (!advancingToSigning) {
      setDisplayStatus(lead.status);
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setDisplayStatus(lead.status);
      toast({
        title: 'Signing is up next',
        description: 'Look out for documents to sign.',
        status: 'info',
        duration: 4000,
        isClosable: false,
        position: toastPosition,
      });
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [lead.status, displayStatus, toast, toastPosition]);

  const stages = resolveStages(displayStatus, stageTimestampsFromTimeline(lead));
  const isTerminal = stages[stages.length - 1].state === 'done';
  const currentIndex = isTerminal
    ? stages.length - 1
    : Math.max(
        stages.findIndex((stage) => stage.state.startsWith('current')),
        0,
      );

  const trackInset = `${50 / stages.length}%`;
  const trackProgress = isTerminal ? 100 : (currentIndex / (stages.length - 1)) * 100;

  const stepProgress = isTerminal ? 100 : ((currentIndex + 0.5) / stages.length) * 100;
  const currentLabel = stages[currentIndex].label;

  return (
    <>
      {/* Desktop: horizontal stepper */}
      <MarketplaceCard display={{ base: 'none', md: 'block' }} px="32px" py="24px">
        <Box position="relative">
          <Box position="absolute" top="14px" left={trackInset} right={trackInset}>
            <ProgressTrack progress={trackProgress} />
          </Box>
          <Flex>
            {stages.map((stage) => {
              const isUpcoming = stage.state === 'upcoming';
              const subtitle = isUpcoming
                ? ''
                : subtitleFor(stage, requiredRemaining, lead.signingStatus);
              return (
                <Flex key={stage.key} direction="column" align="center" flex="1" minW={0}>
                  <StageNode stage={stage} size="32px" />
                  <Box mt="10px">
                    <StageLabelBlock stage={stage} subtitle={subtitle} centered />
                    {stage.key === 'signing' && (
                      <SigningSettlementLine
                        stageState={stage.state}
                        acceptedOffer={acceptedOffer}
                        payoff={lead.payoff}
                        centered
                      />
                    )}
                  </Box>
                </Flex>
              );
            })}
          </Flex>
        </Box>
      </MarketplaceCard>

      {/* Mobile: compact bar, tap to expand the vertical timeline */}
      <MarketplaceCard display={{ base: 'block', md: 'none' }} p={0} overflow="hidden">
        <CollapsibleCard
          header={
            <Flex direction="column" gap="10px" flex="1" minW={0}>
              <Flex align="center" gap="10px">
                <Text fontFamily="heading" fontSize="14px" fontWeight="700" color="leaseEndBlue">
                  Step {currentIndex + 1} of {stages.length}
                </Text>
                <Text fontSize="12px" fontWeight="600" color="grayMedium">
                  {currentLabel}
                </Text>
              </Flex>
              <ProgressTrack progress={stepProgress} />
            </Flex>
          }
        >
          <Box px="18px" pb="16px">
            <Text
              fontFamily="heading"
              fontSize="13px"
              fontWeight="700"
              color="leaseEndBlue"
              mb="10px"
            >
              Your sale timeline
            </Text>
            {stages.map((stage, index) => (
              <Flex key={stage.key} gap="12px">
                <Flex position="relative" direction="column" align="center" flex="none" w="28px">
                  {index < stages.length - 1 && (
                    <Box
                      position="absolute"
                      left="50%"
                      transform="translateX(-50%)"
                      w="2px"
                      bg="platinum"
                      top="14px"
                      bottom={0}
                    />
                  )}
                  <StageNode stage={stage} size="28px" />
                </Flex>
                <Box flex="1" minW={0} pb={index < stages.length - 1 ? '16px' : 0}>
                  <StageLabelBlock
                    stage={stage}
                    subtitle={subtitleFor(stage, requiredRemaining, lead.signingStatus)}
                  />
                  {stage.key === 'signing' && (
                    <SigningSettlementLine
                      stageState={stage.state}
                      acceptedOffer={acceptedOffer}
                      payoff={lead.payoff}
                      showWhenUpcoming
                    />
                  )}
                </Box>
              </Flex>
            ))}
          </Box>
        </CollapsibleCard>
      </MarketplaceCard>
    </>
  );
};

export default SaleProgressTimeline;
